import React, { Component } from 'react';
import Inherit from '../../core/inherit';
import withHooks from '../../core/withHooks';
import RolesController from './roles.controller';
import Page from '../common/page/Page';
import SplitLayout from '../common/layouts/splitLayout/SplitLayout';
import { SplitLayoutVariant, SplitLayoutSide } from '../common/layouts/splitLayout/splitLayout.constants';
import RoleListPanel from './elements/RoleListPanel';
import ScopeAssignmentPanel from './elements/ScopeAssignmentPanel';
import NewRoleModal from './elements/NewRoleModal';
import { SCOPES } from '../../constants/scopes.constants';
import styles from './styles/roles.module.scss';

/**
 * Role Composer — lets an FI's own admins create/rename/delete their roles
 * and assign any number of scopes from the catalog to each one. Gated by
 * the admin:roles:manage scope (not a role name), so it stays correct once
 * an FI renames or restructures its roles.
 * @extends {RolesController}
 * @extends {Component}
 */
class Roles extends Inherit(RolesController, Component) {
    render() {
        const {
            loading,
            roles,
            catalog,
            selectedRoleId,
            saving,
            newRoleModalOpen,
            newRoleName,
            newRoleDescription,
            newRoleCloneFromId,
            creatingRole,
            scopeSearch,
            editRoleModalOpen,
            editingRole,
            editingRoleSubmitting,
        } = this.state;

        const selectedRole = this.getSelectedRole();
        const effectiveScopeIds = this.getEffectiveScopeIds();
        const dirty = this.isDirty();
        // screen:roles:view only grants read access to this screen;
        // every write action (create/rename/delete/set-default/assign
        // scopes) additionally requires admin:roles:manage.
        const canManage = this.hasScope(SCOPES.ROLES_MANAGE);

        return (
            <Page
                isLoading={loading}
                scope={SCOPES.ROLES_VIEW}
                title={{ text: 'Role', highlight: 'Composer' }}
                description="Create roles for your team and control exactly what each one can access."
                notificationContextHolder={this.props.notificationContextHolder}
            >
                <SplitLayout
                    key="roles"
                    variant={SplitLayoutVariant.TWO_THIRDS}
                    sideVariant={SplitLayoutSide.LEFT}
                    embedded
                    className={styles.splitRoot}
                    contentClassName={styles.splitContent}
                    sideClassName={styles.splitSide}
                    side={(
                        <RoleListPanel
                            roles={roles}
                            selectedRoleId={selectedRoleId}
                            onSelectRole={(id) => this.selectRole(id)}
                            onNewRole={() => this.setNewRoleModalOpen(true)}
                            onRenameRole={(id, name) => this.handleRenameRole(id, name)}
                            onEditRole={(role) => this.handleEditRole(role)}
                            onDeleteRole={(id) => this.handleDeleteRole(id)}
                            onSetDefaultRole={(id) => this.handleSetDefaultRole(id)}
                            canManage={canManage}
                        />
                    )}
                >
                    <ScopeAssignmentPanel
                        role={selectedRole}
                        catalog={catalog}
                        effectiveScopeIds={effectiveScopeIds}
                        search={scopeSearch}
                        onSearchChange={(value) => this.setScopeSearch(value)}
                        onToggleScope={(scopeId) => this.toggleScope(scopeId)}
                        dirty={dirty}
                        saving={saving}
                        onSave={this.handleSaveScopes}
                        onDiscard={this.handleDiscardChanges}
                        canManage={canManage}
                    />
                </SplitLayout>
                <NewRoleModal
                    open={newRoleModalOpen}
                    name={newRoleName}
                    description={newRoleDescription}
                    cloneFromId={newRoleCloneFromId}
                    roles={roles}
                    submitting={creatingRole}
                    onNameChange={(value) => this.setNewRoleName(value)}
                    onDescriptionChange={(value) => this.setNewRoleDescription(value)}
                    onCloneFromChange={(value) => this.setNewRoleCloneFromId(value)}
                    onCancel={() => this.setNewRoleModalOpen(false)}
                    onSubmit={this.handleCreateRole}
                />
                <NewRoleModal
                    open={editRoleModalOpen}
                    name={editingRole?.name}
                    description={editingRole?.description}
                    showCloneFrom={false}
                    title={`Edit "${editingRole?.name || ''}"`}
                    helpText="Update this role's name and description."
                    submitText="Save"
                    submittingText="Saving..."
                    submitting={editingRoleSubmitting}
                    onNameChange={(value) => this.setEditingRoleName(value)}
                    onDescriptionChange={(value) => this.setEditingRoleDescription(value)}
                    onCancel={() => this.setEditRoleModalOpen(false)}
                    onSubmit={() => this.handleEditRoleSubmit({
                        roleId: editingRole?.id,
                        name: editingRole?.name,
                        description: editingRole?.description,
                    })}
                />
            </Page>
        );
    }
}

export default withHooks(Roles);
